<template>
  <block-item-card
    ref="dynamicEvolution"
    :title="$t('ALM.Section.DepositsPortfolio.DynamicEvolution.Title')"
    :tooltip="$t('ALM.Section.DepositsPortfolio.DynamicEvolution.InfoTip')"
    class="fill-height d-flex flex-column"
  >
    <v-col class="pa-6 ">
      <v-row>
        <!-- Top Left Chart: Size Total Value of Deposits -->
        <v-col
          cols="12"
          md="6"
          class="gradient-grey-06-bg rounded-lg"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex align-center">
                <div
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-blue-04-base);"
                >
                  {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Size.Title') }}
                </div>
                <div class="chart-subtitle">
                  {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Size.SubTitle') }}
                </div>
              </div>

              <div class="chart-metric">
                <span class="metric-label">{{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.UpdateInterval') }}</span>
                <span
                  class="metric-value"
                  :class="totalValueChange >= 0 ? 'positive' : 'negative'"
                >{{ totalValueChange >= 0 ? '+' : '-' }}{{ formattedTotalValueChange }}</span>
              </div>
            </div>
          </div>
          <e-chart
            :ref="`echart-size-${chartRefId}`"
            :autoresize="true"
            :option="sizeChartOptions"
            :style="`height: 500px;`"
            class="chart"
          />
        </v-col>

        <!-- Top Right Chart: Deposits Number of Accounts -->
        <v-col
          cols="12"
          md="6"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex align-center">
                <h3
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-grey-01-base);"
                >
                  {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Deposits.Title') }}
                </h3>
                <div class="chart-subtitle">
                  {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Deposits.SubTitle') }}
                </div>
              </div>

              <div class="chart-legend">
                <asset-button-group
                  :items="depositTypes"
                  :selected="selectedDepositType"
                  :mandatory="true"
                  :full-width="true"
                  @update:selected="onDepositTypeChange"
                />
              </div>
            </div>
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <div style="height:500px;">
                <e-chart
                  :key="`deposits-chart-${selectedDepositType}-${chartRefId}`"
                  :autoresize="true"
                  :option="depositsChartOptions"
                  :style="`height: 500px;margin-top: 28px;`"
                  class="chart"
                />
              </div>
            </v-lazy>
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-10">
        <!-- Bottom Left Chart: Yield Average Yield Percentage -->
        <v-col
          cols="12"
          md="6"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex align-center">
                <h3
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-purple-02-base);"
                >
                  {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Yield.Title') }}
                </h3>
                <div class="chart-subtitle">
                  {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Yield.SubTitle') }}
                </div>
              </div>

              <div class="chart-metric">
                <span class="metric-label">{{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.UpdateInterval') }}</span>
                <span :class="['metric-value', yieldChange >= 0 ? 'positive' : 'negative']">
                  {{ yieldChange >= 0 ? '+' : '' }}{{ yieldChange }}%
                </span>
              </div>
            </div>
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart
                :ref="`echart-yield-${chartRefId}`"
                :autoresize="true"
                :option="yieldChartOptions"
                :style="`height: 500px;`"
                class="chart"
              />
            </v-lazy>
          </div>
        </v-col>

        <!-- Bottom Right Chart: Interest Expense Annualized -->
        <v-col
          cols="12"
          md="6"

          class="gradient-grey-06-bg  rounded-lg"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex  flex-column">
                <div class="d-flex">
                  <h3
                    class="chart-title"
                    style="color: var(--v-synthesis-ui-orange-02-base);"
                  >
                    {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Interest.Title') }}
                  </h3>
                  <div class="chart-subtitle">
                    {{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.Interest.SubTitle') }}
                  </div>
                </div>
              </div>

              <div class="chart-metric">
                <span class="metric-label">{{ $t('ALM.Section.DepositsPortfolio.DynamicEvolution.UpdateInterval') }}</span>
                <span
                  class="metric-value"
                  :class="interestExpenseChange >= 0 ? 'positive' : 'negative'"
                >{{ interestExpenseChange >= 0 ? '+' : '-' }}{{ formattedInterestExpenseChange }}</span>
              </div>
            </div>
            <e-chart
              :ref="`echart-interest-${chartRefId}`"
              :autoresize="true"
              :option="interestChartOptions"
              :style="`height: 500px;`"
              class="chart"
            />
          </div>
        </v-col>
      </v-row>
    </v-col>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta from '@/mixins/i18n/i18nRouteMeta'
import riskBaseCurrency from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'
import { toNumberFormatNotationShort, toDecimalSignificant, computeYAxisBounds } from '@/lib/utils/helper'

import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent } from 'echarts/components'

use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'ALMDepositsDynamicEvolution',
  components: { BlockItemCard, AssetButtonGroup },
  directives: {},
  mixins    : [i18nRouteMeta, riskBaseCurrency, baseEChartColors, chartsColorMixin, riskAnalysisId],
  props     : {},
  dataStore : {
    appToolbarExtended  : 'App.Config.AppToolbarExtended',
    Deposits            : 'ALM.Deposits.Data',
    riskBaseCurrencyData: 'Risks.BaseCurrency.Data'
  },
  enums: {},
  data () {
    return {
      breakpoint           : (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6),
      chartRefId           : Date.now(),
      selectedDepositType  : 'Total',
      interestOutcomeSwitch: true
    }
  },
  computed: {
    yieldChange () {
      return this.Deposits.DynamicEvolution.yieldChange
    },

    toNumberFormatNotationShort () {
      return toNumberFormatNotationShort
    },

    interestExpenseChange () {
      return this.Deposits.DynamicEvolution.interestExpenseChange
    },

    formattedInterestExpenseChange () {
      const v = toNumberFormatNotationShort(Math.abs(this.interestExpenseChange))

      return `${this.riskBaseCurrency.displaySymbol}${v}`
    },

    formattedTotalValueChange () {
      const v = toNumberFormatNotationShort(Math.abs(this.totalValueChange))

      return `${this.riskBaseCurrency.displaySymbol}${v}`
    },
    dynamicEvolution () {
      return this.Deposits.DynamicEvolution || null
    },

    // Format dates for display (Apr24, May24, etc.)
    formattedDates () {
      if (!this.dynamicEvolution?.dates) return []
      return this.dynamicEvolution.dates.map(date => {
        const d = new Date(date)
        const month = d.toLocaleDateString('en-US', { month: 'short' })
        const year = d.getFullYear().toString().slice(-2)
        return `${month}${year}`
      })
    },

    // Size Total Value Chart Options
    sizeChartOptions () {
      if (!this.dynamicEvolution) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const value =  toNumberFormatNotationShort(params[0].value)
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                      <span>${this.$t('ALM.Section.DepositsPortfolio.DynamicEvolution.Size.Title')}: $${value}</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 35,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          min      : computeYAxisBounds(this.dynamicEvolution.totalValue).min,
          max      : computeYAxisBounds(this.dynamicEvolution.totalValue).max,
          interval : computeYAxisBounds(this.dynamicEvolution.totalValue).interval,
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return this.riskBaseCurrencyDisplayName + (toNumberFormatNotationShort(value) || 0)
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Total Value',
          type     : 'line',
          data     : this.dynamicEvolution.totalValue,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-blue-04')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 10,
          smooth    : true
        }]
      }
    },

    // Deposits Chart Options (Stacked Bar)
    depositsChartOptions () {
      if (!this.dynamicEvolution) return {}

      let series = []

      if (this.selectedDepositType === 'Total') {
        // Show all deposit types stacked
        series = this.dynamicEvolution.typeNames.map((typeName, index) => ({
          name : typeName,
          type : 'bar',
          stack: 'total',
          data : this.dynamicEvolution.amountOfContracts.map(month =>
            month.perTypeAmountOfContracts[index]?.amountOfContracts || 0
          ),
          itemStyle: {
            color: this.getDepositTypeIconColor(index)
          },
          barWidth: '8px'
        }))
      } else {
        // Show only the selected deposit type
        const selectedIndex = this.dynamicEvolution.typeNames.indexOf(this.selectedDepositType)
        if (selectedIndex !== -1) {
          series = [{
            name: this.selectedDepositType,
            type: 'bar',
            data: this.dynamicEvolution.amountOfContracts.map(month =>
              month.perTypeAmountOfContracts[selectedIndex]?.amountOfContracts || 0
            ),
            itemStyle: {
              color: this.getDepositTypeIconColor(selectedIndex)
            },
            barWidth: '8px'
          }]
        }
      }

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            // Custom formatter for deposits chart
            let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`

            // For Total Case, show Total as first number in the list
            if (this.selectedDepositType === 'Total') {
              const totalValue = params.reduce((sum, param) => sum + (param.value || 0), 0)
              result += `<div style="margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${this.themeColorRGBA('synthesis-ui-grey-03')}; margin-right: 8px;"></span>
                <span>Total: ${toDecimalSignificant(totalValue, 5)}</span>
              </div>`
            }

            params.forEach(param => {
              result += `<div style="margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; margin-right: 8px;"></span>
                <span>${param.seriesName}: ${toDecimalSignificant(param.value, 5)}</span>
              </div>`
            })

            return result
          }
        },
        legend: { show: false },
        grid  : {
          left        : 30,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true

        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: { show: false }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -15,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom'
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series
      }
    },

    // Yield Chart Options (Bar Chart)
    yieldChartOptions () {
      if (!this.dynamicEvolution) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                      <span>${this.$t('ALM.Section.DepositsPortfolio.DynamicEvolution.Yield.Title')}: ${params[0].value}%</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 30,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 11
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          },
          splitArea: {
            areaStyle: {
              color     : ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)'],
              shadowBlur: 0.5
            }
          }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value, index) => {
              return value + '%'
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Yield',
          type     : 'bar',
          data     : this.dynamicEvolution.yield,
          itemStyle: {
            color       : this.themeColorRGBA('synthesis-ui-purple-02'),
            borderRadius: [4, 4, 0, 0]
          },
          barWidth: '8px'
        }]
      }
    },

    // Interest Chart Options (Line Chart)
    interestChartOptions () {
      if (!this.dynamicEvolution) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            const value = toNumberFormatNotationShort(params[0].value)
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                      <span>${this.$t('ALM.Section.DepositsPortfolio.DynamicEvolution.Interest.Title')}: $${value}</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 35,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        yAxis: {
          type     : 'value',
          min      : computeYAxisBounds(this.dynamicEvolution.interestExpense).min,
          max      : computeYAxisBounds(this.dynamicEvolution.interestExpense).max,
          interval : computeYAxisBounds(this.dynamicEvolution.interestExpense).interval,
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return this.riskBaseCurrencyDisplayName + (toNumberFormatNotationShort(value) || 0)
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }

        },
        series: [{
          name     : 'Interest Expense',
          type     : 'line',
          data     : this.dynamicEvolution.interestExpense,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-orange-02')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 10,
          smooth    : true
        }]
      }
    },

    sizeChartYAxisMin () {
      if (!this.dynamicEvolution?.totalValue) return undefined

      const values = this.dynamicEvolution.totalValue.filter(val => val !== null && val !== undefined && val !== '-')
      if (values.length === 0) return undefined

      const min = Math.min(...values)
      const max = Math.max(...values)
      const range = max - min

      if (range < max * 0.2) {
        const padding = Math.max(max * 0.2, 1000)
        return Math.max(0, min - padding)
      }

      return Math.max(0, min - range * 0.1)
    },

    totalValueChange () {
      return this.dynamicEvolution?.totalValueChange || 0
    },

    amountOfContractsChange () {
      return this.dynamicEvolution?.amountOfContractsChange || 0
    },

    durationChange () {
      return this.dynamicEvolution?.durationChange || 0
    },

    interestIncomeChange () {
      return this.dynamicEvolution?.interestIncomeChange || 0
    },
    depositTypes () {
      const depositTypes = this.Deposits.DynamicEvolution.typeNames.map((type, index) => ({
        Value: type,
        Title: type,
        icon : {
          color: this.getDepositTypeIconColor(index)
        }
      }))

      return [{ Value: 'Total', Title: 'Total' }, ...depositTypes]
    }

  },

  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }
  },

  created () {},
  beforeMount () {},
  mounted () {},
  beforeUpdate () {},
  updated () {},
  destroyed () {},

  methods: {
    getDepositTypeIconColor (index) {
      return ['#EA804B', '#57C05B', '#C264DE', '#FFB21E'][index % 4]
    },

    onDepositTypeChange (newDepositType) {
      this.selectedDepositType = newDepositType
    }
  }
}
</script>

<style scoped>

.gradient-grey-06-bg {
  background-color: var(--v-synthesis-ui-grey-06-base) !important;
}

.chart-title {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.chart-subtitle {
  margin-left: 8px;
  color: var(--v-synthesis-ui-grey-01-base);
  font-size: 10px;
  position: relative;
  top: 1px;
}

.metric-label {
  font-size: 10px;
  color: var(--v-synthesis-brand-dark-blue-base);
  margin-bottom: 2px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 8px;
}

.chart-metric {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.metric-value {
  font-size: 12px;
  font-weight: 500;
}

.metric-value.positive {
  color: #4CAF50;
  font-size: 28px;
}

.metric-value.negative {
  color: #FF5722;
  font-size: 28px;
}

.chart-legend {
  display: flex;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #666;
}

.legend-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

</style>
